import React, { useEffect, useRef, type ReactNode } from 'react';
import { Ion, Viewer, ScreenSpaceEventType } from 'cesium';
import 'cesium/Build/Cesium/Widgets/widgets.css';
import './3dMain.scss';
import { useCesium } from '../../context/CesiumContext';
import { dropComputeCommandsAfterRender } from '@/helper/computeCommands';
import { trackSplatsForDepthPicking } from '@/helper/translucentDepth';

const CESIUM_TOKEN = import.meta.env.VITE_CESIUM_TOKEN as string;

interface CesiumInitializerProps {
	children: ReactNode;
}

const CesiumInitializer: React.FC<CesiumInitializerProps> = ({ children }) => {
	const cesiumContainerRef = useRef<HTMLDivElement>(null);
	const { viewer, setViewer } = useCesium();

	useEffect(() => {
		if (!cesiumContainerRef.current || viewer) return;

		Ion.defaultAccessToken = CESIUM_TOKEN;

		const newViewer = new Viewer(cesiumContainerRef.current, {
			terrain: undefined,
			baseLayerPicker: false,
			homeButton: false,
			sceneModePicker: false,
			navigationHelpButton: false,
			fullscreenButton: false,
			navigationInstructionsInitiallyVisible: false,
			timeline: false,
			infoBox: false,
			selectionIndicator: false,
			animation: false,
			geocoder: false,
			scene3DOnly: true,
			// useDefaultRenderLoop: true,
			// PERFORMANCE
			requestRenderMode: true,
			maximumRenderTimeChange: Infinity,
			shadows: false,
			skyBox: false,
			shouldAnimate: false,
		});

		const scene = newViewer.scene;
		scene.globe.enableLighting = false;
		// Gaussian splats render without writing depth (and have no ray pick), so the
		// camera controller finds nothing under the cursor and anchors rotation/zoom on
		// the globe or ellipsoid instead of the model — the splat "cannot be grabbed".
		// `pickTranslucentDepth` makes position picks re-render the scene depth-only with
		// depthMask on, so splats become pickable. That extra render costs on every
		// pickPosition, so it is only on while a splat is shown (or underground mode needs it).
		trackSplatsForDepthPicking(scene);
		// Those depth-only re-renders would run the last frame's compute commands again and
		// over-release globe imagery ("This object was destroyed"); see the helper.
		dropComputeCommandsAfterRender(scene);
		// scene.screenSpaceCameraController.enableCollisionDetection = false;

		newViewer.screenSpaceEventHandler.removeInputAction(ScreenSpaceEventType.LEFT_DOUBLE_CLICK);

		newViewer.forceResize();

		setViewer(newViewer);
		(window as any).viewer = newViewer;

		return () => {
			if (newViewer && !newViewer.isDestroyed()) {
				newViewer.destroy();
				setViewer(null);
				(window as any).viewer = null;
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<div ref={cesiumContainerRef} id="cesiumContainer" className="w-full h-full no-select-item">
			{viewer && children}
		</div>
	);
};

export default CesiumInitializer;
